/*--------------------------------------------------------------------------*/
/* Class for creating a receipt to send to the POS print receipt server     */
/* The receipt will have payment items and receipt items, which can be      */
/* either account items or product items to print the receipt to a POS      */
/* printer. BooksDBConstants are passed in from the SERVER.BooksDBConstants */
/*--------------------------------------------------------------------------*/

// Grab the necessary functions for formatting the receipt from csm-common-web
import {
  getAccountName,
  getReceiptDues
} from '../receiptFormatting/receiptFormatting';

/*******************************************************************************
 * getItemAmount */
/**
 * Returns the receiptItem amount for item
 * types that do not have or use the subTotal.
 *
 * @param  receiptItem  Receipt item to get the amount for.
 * @param  BooksDBConstants  Books database constants.
 *******************************************************************************/
function getItemAmount(receiptItem, BooksDBConstants) {
  switch (receiptItem.type) {
    case BooksDBConstants.LIT_DONATION_PURCHASE:
    case BooksDBConstants.LIT_DONATION_RETURN:
    case BooksDBConstants.LIT_JOURNAL_CREDIT:
    case BooksDBConstants.LIT_JOURNAL_DEBIT:
    case BooksDBConstants.LIT_SERVICE_FEE_CREDIT:
    case BooksDBConstants.LIT_RECEIVABLE_CREDIT:
    case BooksDBConstants.LIT_RECEIVABLE_DEBIT:
      return receiptItem.amount;
    default:
      return receiptItem.subTotal || 0;
  }
}

/*---------------------*/
/* Account information */
/*---------------------*/
class AccountItem {
  constructor(accountItem, BooksDBConstants) {
    this.accountID = accountItem.accountID || 0;
    this.accountName = getAccountName(accountItem, BooksDBConstants);
    this.amount = accountItem.amount || 0;
    this.className = accountItem.className || '';
  }
}

/*------------------------------------------------*/
/* Receipt object to be sent to the print server, */
/* including all payment items and receipt items. */
/*------------------------------------------------*/
export class PosPrintReceipt {
  constructor(receipt, BooksDBConstants) {
    /*---------------------------------------------------------------*/
    /* Check if the receipt has cash or check payments. This will be */
    /* used to determine if we should open the cash drawer or not.   */
    /*---------------------------------------------------------------*/
    const hasCashOrCheck = receipt.paymentItems.some(
      (paymentItem) =>
        paymentItem.subType === BooksDBConstants.LIS_CASH_RECEIVED ||
        paymentItem.subType === BooksDBConstants.LIS_CHECK_RECEIVED
    );

    this.amount = receipt.amount || 0;
    this.receiptFrom = receipt.receiptFrom || '';
    this.studentID = receipt.studentID || '';
    this.grade = receipt.grade || 0;

    /*----------------------------------------------------*/
    /* We only print the time stamp on receipts being     */
    /* printed for the first time, not on receipt copies. */
    /*----------------------------------------------------*/
    this.entryDate = receipt.isCopy
      ? receipt.entryDate.toLocaleDateString()
      : receipt.entryDate.toLocaleString() || '';
    this.receiptNumber = receipt.number || '';
    this.studentBalance = receipt.studentBalance;
    this.paymentItems = (receipt.paymentItems || []).map(
      (paymentItem) =>
        new PaymentItem(
          paymentItem,
          receipt.source,
          receipt.type,
          BooksDBConstants
        )
    );
    this.receiptItems = (receipt.receiptItems || []).map(
      (receiptItem) => new ReceiptItem(receiptItem, BooksDBConstants)
    );
    this.memo = receipt.memo || '';
    this.voided = receipt.voided || false;

    /*-----------------------------------------------------------------------*/
    /* If the receipt being printed is a copy, we want to print the word     */
    /* Copy on the receipt. This is also used to determine if we should open */
    /* the cash drawer - we don't want to open the drawer if the receipt is  */
    /* a copy, only when the receipt is being printed for the first time.    */
    /*-----------------------------------------------------------------------*/
    this.isCopy = receipt.isCopy || false;
    this.openCashDrawer = hasCashOrCheck && !receipt.isCopy ? true : false;

    // Calculate the receipt dues
    const { balanceDue, changeDue, balanceReduced } = getReceiptDues(
      receipt,
      BooksDBConstants
    );

    this.balanceDue = balanceDue;
    this.changeDue = changeDue;
    this.balanceReduced = balanceReduced;

    this.subTotal =
      Math.round(
        receipt.receiptItems.reduce(
          (total, receiptItem) =>
            total + getItemAmount(receiptItem, BooksDBConstants),
          0
        ) * 100
      ) / 100;

    this.tax = Number(
      receipt.receiptItems
        .reduce((total, receiptItem) => total + receiptItem.tax, 0)
        .toFixed(2)
    );
  }
}

/*---------------------------*/
/* Options for ReceiptItems. */
/*---------------------------*/
class Option {
  constructor(option = {}) {
    this.itemID = option.itemID || 0;
    this.optionMasterName = option.optionMasterName || '';
    this.optionValue = option.optionValue || '';
  }
}

/*---------------*/
/* Payment items */
/*---------------*/
class PaymentItem {
  constructor(paymentItem, receiptSource, receiptType, BooksDBConstants) {
    this.amount = paymentItem.amount || 0;
    this.name = paymentItem.name || '';
    this.number = paymentItem.number || '';
    this.type = this.getPaymentLabel(
      paymentItem,
      receiptSource,
      receiptType,
      BooksDBConstants
    );
  }
  /*-----------------------------------------*/
  /* Converts the payment type to a string   */
  /* to be displayed on the printed receipt. */
  /*-----------------------------------------*/
  getPaymentLabel(paymentItem, receiptSource, receiptType, BooksDBConstants) {
    // Legacy payment sources. They use the receipt.source values to determine the payment type.
    switch (receiptSource) {
      case BooksDBConstants.RS_REVTRAK:
        return 'RevTrak';
      case BooksDBConstants.RS_SCHOOLPAY:
        return 'School Pay';
      default:
        break;
    }

    switch (paymentItem.type) {
      case BooksDBConstants.LIT_STUDENT_FUNDS_DEBIT:
        return 'Funds Debited';
      case BooksDBConstants.LIT_STUDENT_FUNDS_CREDIT:
        return 'Funds Credited';
      case BooksDBConstants.LIT_JOURNAL_CREDIT:
        if (
          receiptType === BooksDBConstants.LET_RETURN_RECEIPT &&
          paymentItem.subType !== BooksDBConstants.LIS_OFFLINE_RETURN
        )
          return 'Check Written';
        break;
      default:
        break;
    }

    switch (paymentItem.subType) {
      case BooksDBConstants.LIS_CASH_RECEIVED:
      case BooksDBConstants.LIS_CASH_RETURNED:
        return 'Cash';
      case BooksDBConstants.LIS_CHECK_RECEIVED:
      case BooksDBConstants.LIS_CHECK_RETURNED:
        return 'Check';
      case BooksDBConstants.LIS_CREDIT_CARD:
        return 'Credit Card';
      case BooksDBConstants.LIS_DEBIT_CARD:
        return 'Debit Card';
      case BooksDBConstants.LIS_WEBSTORE:
        return 'Webstore';
      case BooksDBConstants.LIS_OFFLINE_RETURN:
        return 'Offline Return Card Record';
      /*---------------------------------------------------------*/
      /* TODO: Ideally this will never happen, but in the off    */
      /* chance it does, we should allow the receipt to be       */
      /* printed. We should at some time get some error          */
      /* handling/notification to let us know when this happens. */
      /*---------------------------------------------------------*/
      default:
        return 'Other';
    }
  }
}

/*------------------*/
/* Item information */
/*------------------*/
class ProductItem {
  constructor(receiptItem, BooksDBConstants) {
    this.amount = getItemAmount(receiptItem, BooksDBConstants) || 0;
    this.itemID = receiptItem.itemID;
    /*-----------------------------------------------------*/
    /* Add the asterisk to any item that is an item order. */
    /*-----------------------------------------------------*/
    this.memo =
      receiptItem.type === BooksDBConstants.LIT_ITEM_ORDER
        ? receiptItem.memo + ' *'
        : receiptItem.memo || '';
    this.name = receiptItem.name || '';
    this.quantity = receiptItem.quantity || 0;
    this.options = (receiptItem.options || []).map(
      (option) => new Option(option)
    );
    this.subItems = (receiptItem.subItems || []).map(
      (subItem) => new ReceiptItem(subItem, BooksDBConstants)
    );
    this.taxable = receiptItem.taxable || false;
  }
}

/*-----------------------------------------------------------------*/
/* Receipt items will either be an AccountItem or ProductListItem. */
/*-----------------------------------------------------------------*/
class ReceiptItem {
  constructor(receiptItem, BooksDBConstants) {
    if (receiptItem.accountID)
      return new AccountItem(receiptItem, BooksDBConstants);
    else return new ProductItem(receiptItem, BooksDBConstants);
  }
}

export const TEST_RECEIPT = {
  amount: 10,
  receiptFrom: 'Test, Student',
  studentID: '12345',
  grade: 11,
  entryDate: new Date().toLocaleDateString(),
  receiptNumber: 1,
  studentBalance: 20,
  memo: 'creating a test receipt in POS.',
  paymentItems: [
    {
      amount: 20,
      name: '',
      number: '',
      type: 'Cash'
    }
  ],
  receiptItems: [
    {
      amount: 10,
      itemID: 3048,
      memo: 'Test Item',
      name: 'Test, Student',
      quantity: 1,
      taxable: true,
      options: [
        {
          itemID: 3048,
          optionMasterName: 'Size, Clothes',
          optionValue: 'S'
        },
        {
          itemID: 3048,
          optionMasterName: 'Color',
          optionValue: 'Green'
        }
      ],
      subItems: []
    }
  ],
  for: '',
  voided: false,
  isCopy: true,
  openCashDrawer: true,
  balanceDue: 0,
  changeDue: 10,
  balanceReduced: 0,
  subTotal: 10,
  tax: 0
};
